// Helpers for the "Identify place" step in PhotoJournalReviewModal.js:
// resolve a photo cluster's coordinates to a city name (reverse geocode)
// and a short list of nearby named real-world places (Wikipedia geosearch),
// so the user can pick what a cluster of photos actually shows instead of
// it staying named after the photo's filename or a generic "New Place".

import Common from "./Common";

// For each cluster (an object with `photos`/`coverIdx`, as both review
// modals' workingClusters are shaped), finds the nearest OTHER cluster by
// straight-line distance between cover-photo coordinates. Lets the review
// UI show "12 km from Kyoto" next to each place, so the user can tell
// whether two clusters are actually the same stop that should be merged,
// without having to cross-reference distances by hand. Returns an array
// aligned with `clusters`; an entry is null only when there's just one
// cluster (nothing to compare against).

export function nearestOtherCluster(clusters, haversineMeters) {
  const coordsOf = (cluster) => {
    const cover = cluster.photos[cluster.coverIdx];
    return { lat: cover.exif_data.latitude, lng: cover.exif_data.longitude };
  };
  return clusters.map((cluster, ci) => {
    const a = coordsOf(cluster);
    let best = null;
    clusters.forEach((other, cj) => {
      if (cj === ci) return;
      const b = coordsOf(other);
      const dist = haversineMeters(a.lat, a.lng, b.lat, b.lng);
      if (best === null || dist < best.dist) best = { dist, index: cj };
    });
    return best;
  });
}

// Same Google Geocoding parsing as HomeHeader.js's ReverseGeocode, but
// awaitable (returns a value) so it can run before a place is named/created
// instead of only patching City/Country onto an already-created place.
export async function reverseGeocodeCoords(lat, lng) {
  try {
    const r = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_GEOCODING_KEY}`
    );
    const json = await r.json();
    let city = "";
    let state = "";
    let country = "";
    for (const result of json.results || []) {
      for (const comp of result.address_components || []) {
        if (comp.types.includes("locality")) city = comp.long_name;
        if (comp.types.includes("administrative_area_level_1")) state = comp.long_name;
        if (comp.types.includes("country")) country = comp.long_name;
      }
      if (city && state && country) break;
    }
    return { city, state, country };
  } catch (e) {
    return { city: "", state: "", country: "" };
  }
}

// Wikipedia's public geosearch API, as a generator combined with
// pageimages/pageterms in one request. MediaWiki's generator=geosearch mode
// drops its own result fields (dist, primary) once other props are merged
// in — verified live against the API 2026-09-22 — but prop=coordinates
// still reliably returns each page's own registered {lat, lon}, so instead
// of a second request just to get a server-computed distance, the distance
// from the cluster's photo is computed locally with the same haversine math
// used elsewhere in this file. (An earlier version read distance from
// `coordinates[0].dist`, which doesn't exist there either — that silently
// produced `undefined` for every candidate: it slipped past a `!== null`
// filter, made the "nearest first" sort a no-op, and rendered as "NaN m".)
async function _geosearchWikipedia(lat, lng, radiusMeters, limit) {
  try {
    const radius = Math.max(10, Math.min(10000, radiusMeters));
    const url =
      `https://en.wikipedia.org/w/api.php?action=query&format=json&origin=*` +
      `&generator=geosearch&ggscoord=${lat}%7C${lng}&ggsradius=${radius}&ggslimit=${limit}` +
      `&prop=coordinates|pageimages|pageterms&piprop=thumbnail&pithumbsize=120`;
    const r = await fetch(url);
    const json = await r.json();
    const pages = (json && json.query && json.query.pages) || {};
    return Object.values(pages)
      .map((p) => {
        const coord = p.coordinates && p.coordinates[0];
        return {
          title: p.title,
          pageid: p.pageid,
          dist: coord ? Common.haversineMeters(lat, lng, coord.lat, coord.lon) : null,
          thumbnail: (p.thumbnail && p.thumbnail.source) || null,
          description: (p.terms && p.terms.description && p.terms.description[0]) || "",
        };
      })
      .filter((c) => c.dist !== null && Number.isFinite(c.dist))
      .sort((a, b) => a.dist - b.dist);
  } catch (e) {
    return [];
  }
}

// Kept as a plain named export for anything that just wants a single-radius
// lookup; findIdentifyCandidates below is what PhotoJournalReviewModal.js
// actually calls.
export async function findNearbyWikipediaPlaces(lat, lng, radiusMeters = 300, limit = 10) {
  return _geosearchWikipedia(lat, lng, radiusMeters, limit);
}

// Two real-world failure modes came up testing this on sparse vs. dense
// areas: a quiet spot (e.g. a rural viewpoint) can come back completely
// empty at a tight radius, while a city center returns a wall of minor
// stub articles (streets, stations, apartment buildings all have Wikipedia
// pages) that's more noise than help. This widens the search radius when
// too few results come back, and once there are *plenty* to choose from,
// prefers pages with a thumbnail image as a rough "actual landmark" signal
// — but never drops down to the photographed-only subset if that would
// leave too few options, so a real match never gets hidden just for
// lacking a photo.
export async function findIdentifyCandidates(lat, lng) {
  const RADII = [150, 500, 1500, 4000];
  const MIN_WANTED = 3;
  const MAX_SHOWN = 6;
  let results = [];
  for (const radius of RADII) {
    results = await _geosearchWikipedia(lat, lng, radius, 15);
    if (results.length >= MIN_WANTED || radius === RADII[RADII.length - 1]) break;
  }
  const withPhoto = results.filter((c) => c.thumbnail);
  const ranked = results.length > MAX_SHOWN && withPhoto.length >= MIN_WANTED ? withPhoto : results;
  return ranked.slice(0, MAX_SHOWN);
}
